import {cdnImage} from '@/util/cdnImage';
import React, {memo} from 'react';
import MUIBox from '@/components/MUI-Components/MUI-Box';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import DisplayImage from '../display-image';
import parse from 'html-react-parser';
import {colors, fontFamily, fontSize, fontWeight, lineHeight, spacing} from '@/styles/tokens';

const check = cdnImage('ifinca-images/check.webp');

// Migrated from globals.scss `.ifinca-coffee-wrapper` (+ mobileScreen/smallmobileScreen/320px
// breakpoints), `.boston-coffee-heading` (+ mobileScreen), `.content-heading` (+ mobileScreen),
// `.coffee-right-image-wrapper`, `.ifinca-head-img`, and `.head-img-ifinca` (+ tabScreen) — all
// single-owner, BostonCoffee's own hardcoded base classNames. Merged with the page-level
// backgroundSx/headingSx/contentHeadingSx/headerImgSx props (every consumer already passes these
// to set its own brand color) via a flat spread — page values always win since they're
// `!important`-guarded, and any property a page doesn't set falls through to these defaults
// unchanged. `.plant-img` (also applied directly by case-study-ifinca's own JSX) and
// `.divder-wrapper`/`.divider-line`/`.divider-margin`/`.perfect-blockchain-divider-wrapper`
// (shared with other components, or sitewide) stay global — out of scope for this component-only pass.
const ifincaCoffeeWrapperDefaultSx = {
    backgroundColor: 'RGB(51, 115, 97)', // case-study brand color, no token match — preserved for parity
    padding: '380px 55px 18px',
    marginTop: '-320px',
    '@media (max-width: 430px)': {
        padding: '120px 30px',
        marginTop: '-113px'
    },
    '@media (max-width: 320px)': {
        padding: '120px 20px',
        marginTop: '-113px'
    }
} as const;

const bostonCoffeeHeadingDefaultSx = {
    fontFamily: `${fontFamily.heading} !important`,
    fontSize: '55px !important', // no exact token match (closest is fontSize[56]) — preserved for parity
    fontWeight: `${fontWeight.bold} !important`,
    color: colors.text.inverse,
    lineHeight: '70px !important', // no exact token match — preserved for parity
    '& span': {color: 'RGB(255, 211, 53)'}, // case-study brand accent, no token match — preserved for parity
    '@media (max-width: 430px)': {
        fontSize: '26px !important', // no exact token match — preserved for parity
        lineHeight: `${lineHeight[40]} !important`
    }
} as const;

const contentHeadingDefaultSx = {
    fontFamily: `${fontFamily.heading} !important`,
    fontSize: `${fontSize[36]} !important`,
    fontWeight: `${fontWeight.bold} !important`,
    color: 'RGB(255, 211, 53)', // case-study brand accent, no token match — preserved for parity
    marginTop: spacing[20],
    '@media (max-width: 430px)': {
        fontSize: '26px !important', // no exact token match — preserved for parity
        lineHeight: `${lineHeight[40]} !important`
    }
} as const;

const coffeeRightImageWrapperSx = {
    zIndex: '-1 !important'
} as const;

const ifincaHeadImgSx = {
    width: '100%',
    textAlign: 'center'
} as const;

const headImgIfincaDefaultSx = {
    width: '900px',
    height: 'auto',
    '@media (max-width: 768px)': {
        width: '100% !important',
        height: 'auto !important',
        padding: '30px !important'
    }
} as const;

const valueDeliveredItemSx = {
    display: 'flex',
    gap: spacing[16],
    marginBottom: spacing[20]
} as const;

const valueDeliveredIconWrapperSx = {
    width: '22px',
    minWidth: '22px',
    flexShrink: 0,
    paddingTop: '25px'
} as const;

const valueDeliveredTextSx = {
    flex: 1,
    '& p': {
        margin: 0
    }
} as const;

const BostonCoffee = (props: any) => {
    const {
        heading,
        valuedelivered,
        subheadingtwo,
        headingclass,
        contentheadingclass,
        backgroundclass,
        contentdescription,
        subheading,
        contentheading,
        headerimg,
        bostonimg,
        bottomimg,
        bottomimgclass,
        imgClassName,
        backgroundSx,
        headingSx,
        contentHeadingSx,
        bottomImgSx,
        headerImgSx
    } = props;

    return (
        <>
            <MUIBox className="plant-img coffee-right-image-wrapper" sx={coffeeRightImageWrapperSx}>
                <DisplayImage src={bostonimg} alt="plantimg" />
            </MUIBox>

            <MUIBox className="ifinca-head-img" sx={ifincaHeadImgSx}>
                <DisplayImage
                    src={headerimg}
                    alt="usa-image"
                    className={`head-img-ifinca ${imgClassName}`}
                    height={671}
                    width={1029}
                    sizes="100vw"
                    quality={100}
                    loading="lazy"
                    sx={{...headImgIfincaDefaultSx, ...headerImgSx}}
                />
            </MUIBox>

            <MUIContainer maxWidth="lg">
                <MUIBox
                    className={`ifinca-coffee-wrapper ${backgroundclass} bottom-spacing`}
                    sx={{...ifincaCoffeeWrapperDefaultSx, ...backgroundSx}}
                >
                    <MUITypography className={`boston-coffee-heading ${headingclass}`} sx={{...bostonCoffeeHeadingDefaultSx, ...headingSx}}>
                        {heading}
                    </MUITypography>

                    <MUIBox className="divder-wrapper perfect-blockchain-divider-wrapper">
                        <hr className="divider-line divider-margin" />
                    </MUIBox>

                    <MUITypography className="blockchain-para color-white">{subheading}</MUITypography>

                    <MUITypography className="blockchain-para color-white">{subheadingtwo}</MUITypography>

                    <MUITypography className="blockchain-para color-white">{contentdescription}</MUITypography>

                    <MUITypography
                        className={`content-heading ${contentheadingclass}`}
                        sx={{...contentHeadingDefaultSx, ...contentHeadingSx}}
                    >
                        {contentheading}
                    </MUITypography>

                    {valuedelivered?.map((data: any, index: number) => (
                        <MUIBox key={index} sx={valueDeliveredItemSx}>
                            <MUIBox sx={valueDeliveredIconWrapperSx}>
                                <DisplayImage
                                    src={check}
                                    alt="check-icon"
                                    loading="lazy"
                                    width={25}
                                    height={25}
                                    style={{
                                        width: '25px',
                                        height: '25px',
                                        objectFit: 'contain',
                                        display: 'block'
                                    }}
                                />
                            </MUIBox>

                            <MUIBox className="blockchain-para color-white" sx={valueDeliveredTextSx}>
                                {data ? parse(data) : ''}
                            </MUIBox>
                        </MUIBox>
                    ))}

                    {bottomimg ? (
                        <MUIBox className={`${bottomimgclass}`} sx={bottomImgSx}>
                            <DisplayImage src={bottomimg} alt="coffe-bean-img" />
                        </MUIBox>
                    ) : null}
                </MUIBox>
            </MUIContainer>
        </>
    );
};

export default memo(BostonCoffee);
